/**
 * hooks/useOnboardingData.ts
 * Onboarding-specific adapter for the shared wizard data provider
 * 
 * Provides onboarding-specific methods while using the shared wizard data context.
 * This gives us the benefits of shared wizard state management with domain-specific APIs.
 */

import { useCallback } from 'react';
import { useWizardData } from '@/components/shared/layouts';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import { markScreenCompleted } from '@/components/Onboarding/services/onboardingDraftUtils';
import type { OnboardingScreenId } from '../types/screens';

/**
 * Onboarding-specific hook that wraps the generic wizard data hook
 * Provides the same API as the old useOnboardingDraft hook for compatibility
 */
export const useOnboardingData = () => {
  const { data: draft, loading, error, updateData, resetData } = useWizardData<OnboardingDraft>();

  // Account-specific update function
  const updateAccount = useCallback((account: Partial<OnboardingDraft['account']>) => {
    updateData((draft: OnboardingDraft) => {
      let updatedAccount = { ...draft.account, ...account };

      // If switching from prop to personal, clear prop firm details
      if (account.accountType === 'personal' && draft.account.accountType === 'prop') {
        const { propFirmDetails: _propFirmDetails, ...accountWithoutProp } = updatedAccount;
        updatedAccount = accountWithoutProp;
      }

      // If account has prop firm details, ensure they're properly merged
      if (account.propFirmDetails) {
        updatedAccount.propFirmDetails = {
          ...draft.account.propFirmDetails,
          ...account.propFirmDetails
        };
      }

      return { ...draft, account: updatedAccount };
    });
  }, [updateData]);

  // Other domain-specific update functions
  const updatePersonal = useCallback((personal: Partial<OnboardingDraft['personal']>) => {
    updateData((draft: OnboardingDraft) => ({
      ...draft,
      personal: { ...draft.personal, ...personal }
    }));
  }, [updateData]);

  const updateBroker = useCallback(async (brokerData: { selectedBrokerId: string; brokerSnapshot?: { brokerId: string; brokerName: string; context: string; }}) => {
    // Detect custom broker and initialize fee configuration accordingly
    const isCustomBroker = brokerData.selectedBrokerId === 'custom-broker';

    // Build account update with broker ID and snapshot
    const accountUpdate: Partial<OnboardingDraft['account']> = {
      selectedBrokerId: brokerData.selectedBrokerId
    };

    if (brokerData.brokerSnapshot) {
      accountUpdate.brokerSnapshot = brokerData.brokerSnapshot;
    }

    // IMPORTANT: Always reset fee configuration when broker changes
    // This prevents flash between steps by ensuring brokerId and feeConfig are atomically consistent
    // DO NOT preserve old feeConfiguration values - start fresh
    const newFeeConfig = {
      choice: isCustomBroker ? 'custom' as const : 'pending' as const,
      isCustomBroker,
      standardFeesAccepted: false,
      configurationComplete: false,
      lastModified: new Date().toISOString()
    };

    // For non-custom brokers, clean config has no custom-specific properties
    if (!isCustomBroker) {
      accountUpdate.feeConfiguration = newFeeConfig;
    } else {
      // Custom brokers keep the full config structure
      accountUpdate.feeConfiguration = newFeeConfig;
    }

    // Update account data - this triggers a single React render with both broker and fee config
    updateAccount(accountUpdate);

    // Small delay to ensure data has propagated before navigation continues
    await new Promise(resolve => setTimeout(resolve, 0));
  }, [updateAccount]);

  const clearBrokerSelection = useCallback(() => {
    updateData((draft: OnboardingDraft) => {
      const updatedAccount = { ...draft.account };
      delete updatedAccount.selectedBrokerId;
      delete updatedAccount.brokerSnapshot;
      return { ...draft, account: updatedAccount };
    });
  }, [updateData]);

  const updateTrading = useCallback((trading: Partial<OnboardingDraft['trading']>) => {
    updateData((draft: OnboardingDraft) => ({
      ...draft,
      trading: { ...draft.trading, ...trading }
    }));
  }, [updateData]);

  const setCurrentScreen = useCallback((screen: OnboardingDraft['currentScreen']) => {
    updateData((draft: OnboardingDraft) => ({ ...draft, currentScreen: screen }));
  }, [updateData]);

  const markCompleted = useCallback((screen: OnboardingScreenId) => {
    updateData((draft: OnboardingDraft) => markScreenCompleted(draft, screen));
  }, [updateData]);

  const setReturnToScreen = useCallback((screen: OnboardingScreenId | undefined, captureSnapshot = false) => {
    updateData((draft: OnboardingDraft) => {
      if (screen === undefined) {
        const { returnToScreen: _, ...rest } = draft;
        return rest;
      }

      const updated: OnboardingDraft = { ...draft, returnToScreen: screen };

      // Capture snapshot when editing (account details or playbooks)
      if (captureSnapshot && screen === 'review') {
        const accountType = draft.account?.accountType;
        const selectedAssetClasses = draft.account?.selectedAssetClasses;
        const playbookTempIds = draft.trading?.playbooks?.map(p => p.tempId);

        updated.editSnapshot = {
          ...(accountType !== undefined && { accountType }),
          ...(selectedAssetClasses !== undefined && { selectedAssetClasses }),
          ...(playbookTempIds !== undefined && { playbookTempIds })
        };
      }

      return updated;
    });
  }, [updateData]);

  const clearReturnToScreen = useCallback(() => {
    updateData((draft: OnboardingDraft) => {
      const { returnToScreen: _, editSnapshot: __, ...rest } = draft;
      return rest;
    });
  }, [updateData]);

  const clearDraft = useCallback(() => {
    resetData();
  }, [resetData]);

  // Utility functions
  const isScreenCompleted = useCallback((screen: OnboardingScreenId) => {
    return draft?.completedScreens.includes(screen) || false;
  }, [draft]);

  const getCompletionPercentage = useCallback(() => {
    if (!draft) return 0;
    const totalScreens = 4;
    const completedCount = draft.completedScreens.length;
    return Math.round((completedCount / totalScreens) * 100);
  }, [draft]);

  const isPropAccountValid = useCallback(() => {
    if (!draft?.account?.propFirmDetails) return false;
    
    const propDetails = draft.account.propFirmDetails;
    
    if (!propDetails.firmName || !propDetails.accountStage) {
      return false;
    }
    
    if (propDetails.firmName === 'other-custom' && !propDetails.customFirmName?.trim()) {
      return false;
    }
    
    return true;
  }, [draft]);

  const isAccountComplete = useCallback(() => {
    if (!draft?.account) return false;
    
    if (!draft.account.accountName.trim()) return false;
    
    if (draft.account.accountType === 'prop') {
      return isPropAccountValid();
    }
    
    return true;
  }, [draft, isPropAccountValid]);

  // Fee configuration methods
  const setFeeConfigurationChoice = useCallback((choice: 'standard' | 'custom', isCustomBroker?: boolean) => {
    updateAccount({
      feeConfiguration: {
        choice,
        isCustomBroker: isCustomBroker || false,
        standardFeesAccepted: choice === 'standard',
        configurationComplete: choice === 'standard', // Standard is immediately complete
        lastModified: new Date().toISOString()
      }
    });
  }, [updateAccount]);

  const updateFeeConfiguration = useCallback((feeConfigs: BrokerFeeConfiguration[]) => {
    updateData((draft: OnboardingDraft) => {
      const currentFeeConfig = draft.account.feeConfiguration || { choice: 'custom' as const };
      
      return {
        ...draft,
        account: {
          ...draft.account,
          feeConfiguration: {
            ...currentFeeConfig,
            choice: 'custom',
            customFeeConfigs: feeConfigs,
            configurationComplete: feeConfigs.length > 0,
            lastModified: new Date().toISOString()
          }
        }
      };
    });
  }, [updateData]);

  const clearFeeConfiguration = useCallback(() => {
    updateData((draft: OnboardingDraft) => {
      const updatedAccount = { ...draft.account };
      delete updatedAccount.feeConfiguration;
      return { ...draft, account: updatedAccount };
    });
  }, [updateData]);

  const isFeeConfigurationComplete = useCallback(() => {
    const feeConfig = draft?.account?.feeConfiguration;
    if (!feeConfig) return false;

    if (feeConfig.choice === 'pending') return false;
    if (feeConfig.choice === 'standard') return feeConfig.standardFeesAccepted === true;
    if (feeConfig.choice === 'custom') return (feeConfig.customFeeConfigs?.length || 0) > 0;

    return false;
  }, [draft]);

  // Risk configuration methods
  const updateRiskConfiguration = useCallback((riskConfig: NonNullable<OnboardingDraft['trading']['riskConfiguration']>) => {
    updateTrading({
      riskConfiguration: riskConfig
    });
  }, [updateTrading]);

  const isRiskConfigurationComplete = useCallback(() => {
    const riskConfig = draft?.trading?.riskConfiguration;
    if (!riskConfig || !riskConfig.profiles || riskConfig.profiles.length === 0) return false;

    // Every profile must have a name and valid account default risk
    return riskConfig.profiles.every(profile =>
      profile.name?.trim() &&
      profile.accountDefault?.type &&
      profile.accountDefault?.value > 0
    );
  }, [draft]);

  return {
    // State - using 'draft' name for backward compatibility
    draft,
    loading,
    error,
    
    // Actions
    updatePersonal,
    updateAccount,
    updateBroker,
    clearBrokerSelection,
    updateTrading,
    setCurrentScreen,
    markCompleted,
    setReturnToScreen,
    clearReturnToScreen,
    clearDraft,

    // Fee configuration actions
    setFeeConfigurationChoice,
    updateFeeConfiguration,
    clearFeeConfiguration,

    // Risk configuration actions
    updateRiskConfiguration,

    // Utilities
    isScreenCompleted,
    getCompletionPercentage,
    isAccountComplete,
    isPropAccountValid,
    isFeeConfigurationComplete,
    isRiskConfigurationComplete
  };
};